import { gsap, ScrollTrigger, prefersReducedMotion } from '@/lib/gsap'

/* ---------------------------------------------------------------
   Shared scroll behaviours.

   Every section still owns its own choreography, but these are the
   moves that recur across the site. Keeping them here means the
   timing relationships stay identical page to page instead of each
   section inventing its own drift speed.
   --------------------------------------------------------------- */

interface ParallaxOptions {
    /* Travel in percent of the element's own height, split either side of centre. */
    amount?: number
    /* Horizontal instead of vertical. */
    axis?: 'y' | 'x'
    scrub?: number
    trigger?: Element
}

export const parallax = (
    targets: gsap.TweenTarget,
    { amount = 8, axis = 'y', scrub = 0.8, trigger }: ParallaxOptions = {}
): gsap.core.Tween | undefined => {
    if (prefersReducedMotion()) {
        return undefined
    }

    const key = axis === 'y' ? 'yPercent' : 'xPercent'

    return gsap.fromTo(targets,
        { [key]: -amount },
        {
            [key]: amount,
            ease: 'none',
            scrollTrigger: {
                trigger: trigger ?? (targets as unknown as Element),
                start: 'top bottom',
                end: 'bottom top',
                scrub
            }
        }
    )
}

/* A frame that opens rather than fades: the mask pulls back from all four
   edges while the picture inside settles out of an over-scale. It is the
   single move that most separates a considered site from a template. */
export const frameOpen = (
    frame: Element,
    inner?: Element | null,
    delay = 0
): void => {
    if (prefersReducedMotion()) {
        gsap.set(frame, { clipPath: 'inset(0% 0% 0% 0%)' })

        return
    }

    const tl = gsap.timeline({
        defaults: { ease: 'noma', duration: 1.35 },
        scrollTrigger: {
            trigger: frame,
            start: 'top 88%',
            once: true
        },
        delay
    })

    tl.fromTo(frame,
        { clipPath: 'inset(16% 12% 16% 12% round var(--radius-frame))' },
        { clipPath: 'inset(0% 0% 0% 0% round var(--radius-frame))' }
    )

    if (inner) {
        tl.fromTo(inner, { scale: 1.22 }, { scale: 1, duration: 1.7 }, 0)
    }
}

/* Counts a numeric label up as it scrolls into view. The reference uses this
   on every statistic, and it reads as intent rather than decoration. */
export const countUp = (
    node: HTMLElement,
    to: number,
    { prefix = '', suffix = '', decimals = 0 }: { prefix?: string; suffix?: string; decimals?: number } = {}
): void => {
    const format = (value: number): string => `${prefix}${value.toFixed(decimals)}${suffix}`

    if (prefersReducedMotion()) {
        node.textContent = format(to)

        return
    }

    const state = { value: 0 }

    gsap.to(state, {
        value: to,
        duration: 1.8,
        ease: 'noma',
        onUpdate: () => {
            node.textContent = format(state.value)
        },
        scrollTrigger: {
            trigger: node,
            start: 'top 92%',
            once: true
        }
    })
}

/* Rows that arrive one after another from below a shared mask. Used by every
   ruled register on the site (clients, FAQ, journal, awards). */
export const rowsIn = (
    rows: ArrayLike<Element>,
    trigger: Element,
    stagger = 0.07
): void => {
    if (rows.length === 0) {
        return
    }

    if (prefersReducedMotion()) {
        gsap.set(rows, { opacity: 1, y: 0 })

        return
    }

    gsap.fromTo(rows,
        { opacity: 0, y: 34 },
        {
            opacity: 1,
            y: 0,
            duration: 1.05,
            stagger,
            ease: 'noma',
            scrollTrigger: {
                trigger,
                start: 'top 82%',
                once: true
            }
        }
    )
}

export { ScrollTrigger }
